import { useLayoutEffect, useMemo, useRef, useState } from "react";

// Recharts' default Tooltip anchors by a corner (offset from the active
// coordinate) instead of centering on the hovered bar/point — see
// ReportsWeekdayChart.js's original comment (viewAtoms.js's TdeeReferenceLabel
// header has the same note) for the full "why" a custom `position` is needed.
// Extracted here once the same block was duplicated near-verbatim across
// ReportsWeekdayChart.js, ReportsMacroChart.js and ReportsIntakeChart.js.
//
// Usage: attach `containerRef` to the div wrapping the chart's
// ResponsiveContainer, spread `chartHandlers` onto the chart component
// (BarChart/ComposedChart), pass `position={tooltipPosition}` to <Tooltip>,
// and pass `onMeasureWidth` down into the tooltip content component (which
// should call it from its own `useMeasuredTooltipRef`, below).
export function useCenteredTooltip({ initialWidth = 160, edgeMargin = 4 } = {}) {
  const containerRef = useRef(null);
  const [tooltipWidth, setTooltipWidth] = useState(initialWidth);
  const [activeX, setActiveX] = useState(null);

  // When the tooltip is active, set the activeX state to the x coordinate of the active coordinate
  const handleMouseMove = (state) => {
    if (state?.isTooltipActive && state.activeCoordinate) {
      setActiveX(state.activeCoordinate.x);
    }
  };
  // When the tooltip is not active, set the activeX state to null
  const handleMouseLeave = () => setActiveX(null);

  const tooltipPosition = useMemo(() => {
    if (activeX == null) return undefined;
    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    const raw = activeX - tooltipWidth / 2;
    const max = Math.max(edgeMargin, containerWidth - tooltipWidth - edgeMargin);
    return { x: Math.min(Math.max(raw, edgeMargin), max) };
  }, [activeX, tooltipWidth, edgeMargin]);

  return {
    containerRef,
    tooltipPosition,
    onMeasureWidth: setTooltipWidth,
    chartHandlers: { onMouseMove: handleMouseMove, onMouseLeave: handleMouseLeave },
  };
}

// Companion for the tooltip CONTENT component (the one passed as Recharts'
// `content={<MyTooltip onMeasureWidth={...} />}`) — measures its own
// rendered width on every render (content can vary in length) and reports it
// up via the `onMeasureWidth` this component receives as a prop. Attach the
// returned ref to the tooltip's outermost box element.
export function useMeasuredTooltipRef(onMeasureWidth) {
  const boxRef = useRef(null);
  useLayoutEffect(() => {
    if (boxRef.current) onMeasureWidth?.(boxRef.current.offsetWidth);
  });
  return boxRef;
}

export default useCenteredTooltip;
